import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {
    CustomerMetricsDeleteResponse,
    CustomerMetricsDTO,
    CustomerMetricsType,
    ExportCustomerMetricsResponse,
    UploadCustomerMetricsResponse,
} from '@callmart/types';
import { QueryKey as QueryKeys } from '../../../enums/QueryKey';
import {
    deleteCustomerMetrics,
    exportCustomerMetrics,
    getCustomerMetrics,
    uploadCustomerMetrics,
} from '../../../services/forecasts';
import { useDispatchErrorNotification } from '../../useDispatchErrorNotification';
import { useDispatchSuccessNotification } from '../../useDispatchSuccessNotification';
import { queryClient } from '../../../services/queryClient';
import { downloadBlobFile } from '../../../utils/fileUtils';

export const useUploadCustomerMetrics = () => {
    const { t } = useTranslation('forecasts');
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();

    return useMutation<UploadCustomerMetricsResponse[], AxiosError, File>({
        mutationFn: uploadCustomerMetrics,
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKeys.CustomerMetrics]);
            dispatchSuccessNotification({ message: t('notification.fileUploaded') });
        },
        onError: (error) => {
            dispatchErrorNotification({ message: t(error.message) });
        },
    });
};

export const useExportCustomerMetrics = () => {
    const { t } = useTranslation('forecasts');
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();

    return useMutation<ExportCustomerMetricsResponse, AxiosError, CustomerMetricsType | undefined>({
        mutationFn: exportCustomerMetrics,
        onError: (error) => {
            dispatchErrorNotification({ message: t(error.message) });
        },
        onSuccess: (data) => {
            dispatchSuccessNotification({ message: t('notification.fileGenerated') });
            downloadBlobFile(data);
        },
    });
};

export const useCustomerMetrics = ({ type }: { type: CustomerMetricsType }) => {
    return useQuery<CustomerMetricsDTO[], AxiosError>({
        refetchOnWindowFocus: false,
        // scalar `type` (not the params object) keeps the key shape [CustomerMetrics, type],
        // so useDeleteCustomerMetrics's invalidateQueries([CustomerMetrics, type]) still matches.
        queryKey: [QueryKeys.CustomerMetrics, type],
        queryFn: () => getCustomerMetrics({ type }),
        initialData: () => Array<CustomerMetricsDTO>(),
    });
};

export const useDeleteCustomerMetrics = () => {
    const { t } = useTranslation('forecasts');
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();
    const { dispatchErrorNotification } = useDispatchErrorNotification();

    return useMutation<CustomerMetricsDeleteResponse, AxiosError, { ids: number[]; type?: CustomerMetricsType }>({
        mutationFn: deleteCustomerMetrics,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries([QueryKeys.CustomerMetrics].concat(variables.type || []));
            dispatchSuccessNotification({ message: t('notification.valuesDeleted') });
        },
        onError: (error) => {
            dispatchErrorNotification({ message: t(error.message) });
        },
    });
};
